import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, getRelatedArticles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-500"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-200">作品未找到</h1>
          <p className="mt-2 text-foreground-400">您访问的作品可能已被移除或链接无效</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, 4);
  const categoryMap = Object.fromEntries(categories.map((c) => [c.slug, c.name]));
  const categoryName = categoryMap[article.category] || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '星空传媒',
    },
    publisher: {
      '@type': 'Organization',
      name: '星空传媒',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://liveorbit.github.io/article/${article.slug}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <SEO
        title={`${article.title} — 星空传媒香菱作品`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},香菱出演,星空传媒作品`}
        canonicalUrl={`https://liveorbit.github.io/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
      />

      {/* Header */}
      <div className="pt-20 pb-8 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <h1 className="mt-6 font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-100 leading-tight">
            {article.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i> {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i> {article.duration}
            </span>
            <span className="flex items-center gap-1.5 text-primary-400">
              <i className="ri-star-fill"></i> {article.rating} 分
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i> {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次观看
            </span>
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-0.5 rounded-md bg-accent-100/30 text-accent-400 text-xs whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8 md:py-10 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Cover Image */}
          <div className="rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full aspect-video object-cover"
              title={article.title}
            />
          </div>

          {/* Article Body */}
          <article className="prose-custom">
            {article.content.split('\n\n').map((paragraph, idx) => {
              if (!paragraph.trim()) return null;
              return (
                <p key={idx} className="text-foreground-300 leading-relaxed mb-4 text-[15px]">
                  {paragraph.trim()}
                </p>
              );
            })}
          </article>

          {/* Meta Info */}
          <div className="mt-10 p-5 rounded-xl bg-background-100 border border-background-200/70">
            <h3 className="font-heading text-base font-semibold text-foreground-200 mb-3">作品信息</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-foreground-500">导演：</span>
                <span className="text-foreground-300">{article.director}</span>
              </div>
              <div>
                <span className="text-foreground-500">主演：</span>
                <span className="text-foreground-300">{article.cast.join('、')}</span>
              </div>
              <div>
                <span className="text-foreground-500">时长：</span>
                <span className="text-foreground-300">{article.duration}</span>
              </div>
              <div>
                <span className="text-foreground-500">发布：</span>
                <span className="text-foreground-300">{article.publishDate}</span>
              </div>
              <div>
                <span className="text-foreground-500">分类：</span>
                <Link
                  to={`/category/${article.category}`}
                  className="text-primary-400 hover:text-primary-300 transition-colors"
                >
                  {categoryName}
                </Link>
              </div>
              <div>
                <span className="text-foreground-500">评分：</span>
                <span className="text-primary-400">{article.rating} / 10</span>
              </div>
            </div>
          </div>

          {/* Share */}
          <div className="mt-6 flex items-center gap-3">
            <span className="text-sm text-foreground-400">分享：</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer">
              <i className="ri-weibo-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer">
              <i className="ri-wechat-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer">
              <i className="ri-link"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Related Articles */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-6">
            相关作品推荐
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {relatedArticles.map((rel) => (
              <ArticleCard
                key={rel.id}
                {...rel}
                categoryName={categoryMap[rel.category] || rel.category}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}